import type { MoveCanvasInTree } from "@/api/canvas";
import type { FileRecord, MoveFileRecordPayload } from "@cohesive/api-client";
import { getFolderDescendants } from "./folderDelete";

/** Backend `move` mutation invoked once per record in a cascade. */
export type MoveOne = (vars: {
  fileRecordId: string;
  payload: MoveFileRecordPayload;
}) => Promise<unknown>;

interface CascadeMoveDestination {
  /** Destination scope components (workspace / canvas / flow-session). */
  scope: Omit<MoveFileRecordPayload["destination"], "path" | "is_folder">;
  /** Full destination path of the folder marker. Must end with `"/"`. */
  folderPath: string;
}

function destinationHasScope(
  destination: MoveFileRecordPayload["destination"],
): boolean {
  if (destination.class === "workspace") return !!destination.workspace_id;
  if (destination.class === "canvas") return !!destination.canvas_id;
  return !!destination.flow_session_id;
}

/**
 * Move a folder and every record nested inside it. The backend's move
 * primitive only updates the row it targets, so moving just the folder
 * marker leaves the children orphaned at their original paths — and the
 * destination folder appears empty. We rewrite each descendant's path
 * relative to the new folder location and fire the moves in parallel.
 *
 * Concurrency mirrors `cascadeDeleteFolder`: every descendant move runs
 * via `Promise.all`, then the folder marker itself moves last so a
 * partial failure leaves the source folder visible with its remaining
 * children (rather than a dangling marker pointing at the new location
 * with stale orphans behind it). `Promise.all` rejects on first failure,
 * which the caller surfaces as a toast.
 */
export async function cascadeMoveFolder(
  folder: FileRecord,
  allRecords: FileRecord[],
  destination: CascadeMoveDestination,
  moveOne: MoveOne,
  moveCanvas: MoveCanvasInTree,
): Promise<void> {
  if (!folder.path.endsWith("/")) {
    throw new Error("cascadeMoveFolder: source record is not a folder");
  }
  if (!destination.folderPath.endsWith("/")) {
    throw new Error(
      "cascadeMoveFolder: destination folderPath must end with '/'",
    );
  }

  const descendants = getFolderDescendants(folder, allRecords);
  const sourcePrefix = folder.path;
  const destPrefix = destination.folderPath;

  // The folder marker moves FIRST: a destination collision (renaming onto an
  // existing sibling, moving onto a same-named folder) fails fast here, before
  // a single child has been re-filed — never a silent merge with the parent
  // left behind (4.12).
  await moveOne({
    fileRecordId: folder.id,
    payload: {
      destination: {
        ...destination.scope,
        path: destPrefix,
        is_folder: true,
      },
    },
  });

  await Promise.all(
    descendants.map((record) => {
      const relative = record.path.slice(sourcePrefix.length);
      const newPath = `${destPrefix}${relative}`;
      const isFolder = record.path.endsWith("/");
      // A canvas travels with its folder through the canvas API.
      if (record.kind === "canvas") {
        const canvasId = record.canvas?.id;
        if (!canvasId) return Promise.resolve();
        return moveCanvas({
          canvasId,
          parentPath: newPath.slice(0, newPath.lastIndexOf("/") + 1),
          workspaceId: destination.scope.workspace_id,
        });
      }
      return moveOne({
        fileRecordId: record.id,
        payload: {
          destination: {
            ...destination.scope,
            path: newPath,
            is_folder: isFolder || undefined,
          },
        },
      });
    }),
  );
}

/**
 * Convenience: branch on file vs folder and dispatch the right primitive.
 * For a file, just calls `moveOne` with the supplied payload. For a
 * folder, delegates to `cascadeMoveFolder` so descendants travel with it.
 *
 * The expected `payload.destination.path` is the *full* destination path
 * (i.e. the new path of the source record, with a trailing `/` for
 * folders), exactly the shape callers already build for `moveOne`.
 */
export async function moveRecordCascading(params: {
  record: FileRecord;
  payload: MoveFileRecordPayload;
  allRecords: FileRecord[];
  moveOne: MoveOne;
  moveCanvas: MoveCanvasInTree;
}): Promise<void> {
  const { record, payload, allRecords, moveOne, moveCanvas } = params;

  if (!record.path.endsWith("/")) {
    await moveOne({ fileRecordId: record.id, payload });
    return;
  }

  if (!destinationHasScope(payload.destination)) {
    // Missing the destination scope id. Fall back to a single move so the
    // caller still sees the request go through and backend validation surfaces.
    await moveOne({ fileRecordId: record.id, payload });
    return;
  }

  await cascadeMoveFolder(
    record,
    allRecords,
    {
      scope: {
        class: payload.destination.class,
        workspace_id: payload.destination.workspace_id,
        canvas_id: payload.destination.canvas_id,
        flow_session_id: payload.destination.flow_session_id,
      },
      folderPath: payload.destination.path,
    },
    moveOne,
    moveCanvas,
  );
}
